"use client"

import { ResultEmailCaptureCard } from "@/components/tools/shared/result-email-capture-card"
import { getCurrentMonthIndex, getMonthName } from "@/data/guide-monthly-tasks"
import { getRegionFromZipCode } from "@/data/regional-climate"

/** Month the seasonal plan is offered for. */
export function getPlanMonthName(): string {
  return getMonthName(getCurrentMonthIndex())
}

export interface InconclusivePlanAskProps {
  /**
   * Capture source, also sent as the `/api/email/subscribe` context. Every
   * source used here must be listed in that route's `CARE_PLAN_CONTEXTS` or the
   * inbox gets the generic welcome instead of the seasonal plan this copy names.
   */
  source: string
  zipCode?: string | null
  /** OAuth return target. Without one the ask renders as the email-only card. */
  redirectTo?: string
  onBeforeOAuth?: () => void
  /** Fires once the email lane captures successfully, so a host result screen
   *  can report this offer as the next action it advertised. */
  onCaptured?: () => void
  className?: string
}

/**
 * The offer for a result that produced no verdict.
 *
 * The ask is the current month's seasonal plan for the user's ZIP, which is true
 * whatever the photo showed, so nothing here depends on the identification that
 * failed. It promises no human review: nobody works the `analysis_review_cases`
 * queue, so a "we will look at it" line would be a lie.
 */
export function InconclusivePlanAsk({
  source,
  zipCode,
  redirectTo,
  onBeforeOAuth,
  onCaptured,
  className,
}: InconclusivePlanAskProps) {
  const monthName = getPlanMonthName()
  const trimmedZip = zipCode?.trim() || ""
  const region = trimmedZip ? getRegionFromZipCode(trimmedZip) : ""
  const regionLabel = region && region !== "Unknown" ? region : ""

  const headline = `Get the ${monthName} plan for ${trimmedZip || "your lawn"}`
  const subcopy = regionLabel
    ? `Mowing height, watering depth and what not to apply this month in ${regionLabel} lawns. This does not depend on what the photo showed.`
    : "Mowing height, watering depth and what not to apply this month. This does not depend on what the photo showed."
  const successSubcopy = trimmedZip
    ? `Your 12-month plan is on the way, timed to ${trimmedZip} and starting with this month. Your photo stays right here to add to.`
    : "Your 12-month lawn plan is on the way, starting with this month. Your photo stays right here to add to."

  return (
    <div className={className}>
      <ResultEmailCaptureCard
        source={source}
        toolDisplayName="seasonal lawn plan"
        headline={headline}
        subcopy={subcopy}
        successSubcopy={successSubcopy}
        zipCode={trimmedZip || null}
        onCaptured={onCaptured}
        ctaLabel="Send it"
        accountLead={
          redirectTo
            ? {
                redirectTo,
                headline,
                subcopy,
                dividerLabel: "or get it by email",
                // Nothing has been identified to "save", so the account labels
                // stay neutral.
                googleLabel: "Continue with Google",
                appleLabel: "Continue with Apple",
                onBeforeOAuth,
              }
            : undefined
        }
      />
    </div>
  )
}

export default InconclusivePlanAsk
